import React from 'react';
import {
  Band,
  Marker,
  MarkerArrow,
  MarkerPill,
  MarkerRow,
  Scale,
  Segment,
  Wrapper,
} from './styles';

export type ScoreKind = 'PF' | 'PJ';

/**
 * Faixas de classificação da Assertiva, por tipo de documento. Os limites são
 * do provedor — pessoa jurídica usa uma escala bem diferente da de pessoa
 * física (a faixa A começa em 800 para PJ e em 850 para PF, e as faixas de
 * baixo são muito mais estreitas para PJ).
 *
 * Na ordem pior → melhor, que é a ordem em que a régua é desenhada: o olho lê
 * da esquerda para a direita e a leitura "quanto mais à direita, melhor"
 * dispensa explicação.
 */
const RANGES: Record<ScoreKind, Array<[string, number, number]>> = {
  PJ: [
    ['F', 0, 17],
    ['E', 18, 130],
    ['D', 131, 293],
    ['C', 294, 490],
    ['B', 491, 799],
    ['A', 800, 1000],
  ],
  PF: [
    ['F', 0, 179],
    ['E', 180, 349],
    ['D', 350, 519],
    ['C', 520, 689],
    ['B', 690, 849],
    ['A', 850, 1000],
  ],
};

/* Tons mais fechados que o verde/vermelho vivos usados antes: estes carregam
   texto branco por cima (a letra da classe) e precisam passar em contraste —
   os vivos davam em torno de 2:1. */
export const CLASS_COLORS: Record<string, string> = {
  A: '#1F6B31',
  B: '#3F7C2F',
  C: '#8D6100',
  D: '#A05B14',
  E: '#A8322A',
  F: '#7A1C16',
};

export const colorForClass = (cls?: string | null): string =>
  CLASS_COLORS[(cls ?? '').toUpperCase()] ?? 'var(--stone-500)';

interface ScoreBandProps {
  /** Pontuação apurada. 0 ou nulo desenha a régua como legenda, sem alfinete. */
  score?: number | null;
  /** Classe apurada (A–F), destacada com o anel interno. */
  cls?: string | null;
  kind: ScoreKind;
  /** Versão reduzida, para telas estreitas: sem os intervalos dentro da faixa. */
  compact?: boolean;
  scaleLabel?: string;
}

const ScoreBand: React.FC<ScoreBandProps> = ({
  score,
  cls,
  kind,
  compact = false,
  scaleLabel,
}) => {
  const points = Number(score ?? 0);
  const active = (cls ?? '').toUpperCase();
  const hasMarker = points > 0;

  // 1001 e não 1000: uma pontuação máxima cairia exatamente na borda direita e
  // o alfinete sairia meio cortado. O clamp em 3–97% protege as duas pontas.
  const markerPosition = Math.max(
    3,
    Math.min(97, (points / 1001) * 100),
  );

  return (
    <Wrapper>
      <MarkerRow compact={compact}>
        {hasMarker && (
          <Marker position={markerPosition}>
            <MarkerPill>
              <b>{points}</b>
              <span>pts</span>
            </MarkerPill>
            <MarkerArrow />
          </Marker>
        )}
      </MarkerRow>

      <Band compact={compact}>
        {RANGES[kind].map(([letter, min, max]) => (
          <Segment
            key={letter}
            weight={max - min + 1}
            color={CLASS_COLORS[letter]}
            active={letter === active}
            compact={compact}
          >
            <strong>{letter}</strong>
            {!compact && (
              <span>{letter === 'A' ? `${min}+` : `${min}–${max}`}</span>
            )}
          </Segment>
        ))}
      </Band>

      <Scale>
        <span>0</span>
        {scaleLabel && <span>{scaleLabel}</span>}
        <span>1000</span>
      </Scale>
    </Wrapper>
  );
};

export default ScoreBand;
